import React, { CSSProperties, FunctionComponent, useEffect, useRef } from 'react'
import c3, { ChartAPI, ChartConfiguration, Primitive, PrimitiveArray } from 'c3'
import { useChartResize } from './useChartResize'
import './DonutChart.css'

interface Props {
  id: string
  style?: CSSProperties
  data: Array<[string, ...PrimitiveArray]>
  // title is rendered in the centre of the donut (e.g. the total).
  title?: string
  legend?: ChartConfiguration['legend']
  size?: ChartConfiguration['size']
  /** tooltips show each segment's own value (e.g. a count) rather than c3's default percentage */
  rawValues?: boolean
  /** segment colours, in order; defaults to the System health palette (as before) */
  colors?: string[]
}

const availableColors = ['#26AA27', '#8EC641', '#33C1AF', '#F0C002']

export const DonutChart: FunctionComponent<Props> = props => {
  const elementRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<ChartAPI | null>(null)
  // redraw only when what's drawn changes, not whenever the parent passes equal new objects
  const configKey = JSON.stringify([
    props.id,
    props.data,
    props.title,
    props.legend,
    props.size,
    props.rawValues,
    props.colors,
  ])
  // Charts given a height but no width follow their container through useChartResize (smooth, per frame).
  // Charts without a size keep c3's own defaults, as they always have (e.g. the System health tab).
  const followsContainer = Boolean(props.size) && props.size?.width === undefined
  useChartResize(elementRef, chartRef, followsContainer)

  useEffect(() => {
    const colors: any = {}
    const palette = props.colors ?? availableColors
    props.data.forEach((d, i) => {
      colors[d[0]] = palette[i % palette.length]
    })
    const chart = c3.generate({
      bindto: '#' + props.id,
      size: props.size,
      data: {
        columns: props.data,
        type: 'donut',
        colors: colors,
      },
      donut: {
        title: props.title,
      },
      // hidden unless asked for: the System health tab lists the values beside the chart instead
      legend: props.legend || { show: false },
      tooltip: props.rawValues ? { format: { value: (value: Primitive) => String(value) } } : undefined,
      resize: { auto: !followsContainer }, // useChartResize follows the container instead
    })
    chartRef.current = chart
    return () => {
      chartRef.current = null
      chart.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- configKey stands in for the props it serialises
  }, [configKey, followsContainer])

  // eslint-disable-next-line react/forbid-dom-props -- pass-through component style prop
  return <div ref={elementRef} id={props.id} style={props.style} />
}
